import React from 'react';
import Link from 'next/link';
import round from 'lodash/round';
import endsWith from 'lodash/endsWith';
import GeneralChart from '@/components/city/GeneralChart';
import { CityContext } from '@/context/create-context';
import { TAB_NEAR_CITY } from '@/constants/city';
import { PRODUCTION_HOST } from '@/constants/router-path';
import { trackClickEvent } from '@/utils/site-track';
import PostIcon from '@/assets/city/post.svg';
const NearCity = () => {
  const { name, adjacent_cities: adjacentCities } =
    React.useContext(CityContext);

  const [showMedian, setShowMedian] = React.useState(false);

  const hasCurrentCity =
    adjacentCities.map((item) => item.current_city).filter(Boolean).length > 0;

  const seoInfo = React.useMemo(() => {
    if (adjacentCities.length === 0) return null;
    const nearCity = adjacentCities
      .slice(0, 6)
      .filter((item) => !item.current_city);
    const currentCity = adjacentCities.find((item) => item.current_city);
    const topCity = nearCity[0];

    return `根据近六个月数据统计，与${name}平均薪资相近的城市包括${nearCity
      .map(({ name }) => (endsWith(name, '市') ? name.slice(0, -1) : name))
      .join('、')}等城市。其中${topCity.name}的平均薪资最高，为¥${round(
      topCity.avg,
      -2,
    )}。在${name}招聘市场中热度最高的三个行业分别是${currentCity?.market_share_top_three
      .map(({ name }) => name)
      .join('、')}，在其中${
      currentCity?.highest_salary_job.name
    }这一岗位的薪资最高，为¥${round(
      currentCity?.highest_salary_job.avg || 0,
      -2,
    )}/月。本数据统计来源于各大平台发布的公开数据，仅供参考。`;
  }, [adjacentCities]);

  React.useEffect(() => {
    setShowMedian(window.location.host !== PRODUCTION_HOST);
  }, []);

  if (!hasCurrentCity) {
    return null;
  }

  return (
    <GeneralChart title={`${name}相邻排名招聘对比`} seoInfo={seoInfo}>
      <table className="w-full border-separate border-spacing-y-2 whitespace-nowrap text-sm leading-5.5">
        <thead>
          <tr className="text-left">
            <th className="pl-4 pb-4 pt-0 pr-2 font-normal">月薪排名</th>
            <th className="pb-4 pt-0 pr-4 font-normal">城市</th>
            <th className="pb-4 pt-0 pr-4 font-normal">月薪</th>
            {showMedian && (
              <th className="pb-4 pt-0 pr-4 font-normal">平均中位</th>
            )}
            <th className="pb-4 pt-0 pr-4 font-normal">市场占比前三的岗位</th>
            <th className="pr-10 pb-4 pt-0 font-normal">薪资最高的职位</th>
          </tr>
        </thead>
        <tbody>
          {adjacentCities.map(
            ({
              rank,
              id,
              name,
              pinyin,
              avg,
              avg_median,
              highest_salary_job: {
                avg: highestAvg,
                id: highestId,
                name: highestName,
              },
              market_share_top_three,
              current_city,
            }) => (
              <tr
                key={id}
                data-selected={current_city ? '' : undefined}
                className="group text-left"
              >
                <td className="pl-4 py-3.5 bg-white/80 rounded-l-lg group-hover:bg-blue-100 group-hover:bg-opacity-70 group-data-[selected]:bg-blue-100 group-data-[selected]:bg-opacity-70">
                  <span
                    className={`inline-block text-sm px-2.5 py-0.5 rounded-full ${
                      current_city ? 'bg-blue-300' : 'bg-blue-100/70'
                    }`}
                  >
                    {rank}
                  </span>
                </td>
                <td className="py-3.5 pr-4 bg-white bg-opacity-80 underline group-hover:bg-blue-100 group-hover:bg-opacity-70 group-data-[selected]:bg-blue-100 group-data-[selected]:bg-opacity-70">
                  <Link
                    href={`/${pinyin}/`}
                    data-ele-group={`${TAB_NEAR_CITY}_city`}
                    data-ele-id={`city_${name}`}
                    onClick={trackClickEvent}
                  >
                    {name}
                  </Link>
                </td>
                <td className="py-3.5 pr-4 bg-white bg-opacity-80 group-hover:bg-blue-100 group-hover:bg-opacity-70 group-data-[selected]:bg-blue-100 group-data-[selected]:bg-opacity-70">
                  {`￥${round(avg, -2)}`}
                </td>
                {showMedian && (
                  <td className="py-3.5 pr-4 bg-white bg-opacity-80 group-hover:bg-blue-100 group-hover:bg-opacity-70 group-data-[selected]:bg-blue-100 group-data-[selected]:bg-opacity-70">
                    {`￥${round(avg_median || 0, -2)}`}
                  </td>
                )}
                <td className="py-3.5 pr-4 bg-white bg-opacity-80 group-hover:bg-blue-100 group-hover:bg-opacity-70 group-data-[selected]:bg-blue-100 group-data-[selected]:bg-opacity-70">
                  {market_share_top_three.map(({ name }) => name).join('、')}
                </td>
                <td className="py-3.5 pl-0 pr-4 bg-white bg-opacity-80 rounded-r-lg group-hover:bg-blue-100 group-hover:bg-opacity-70 group-data-[selected]:bg-blue-100 group-data-[selected]:bg-opacity-70">
                  <i
                    className="w-5 h-5 inline-block relative top-[5px] mr-1 bg-contain bg-no-repeat bg-center"
                    style={{ backgroundImage: `url(${PostIcon.src})` }}
                  />
                  <Link href={`/${pinyin}/${highestId}/`}>{highestName}</Link>
                  {` ￥${round(highestAvg, -2)}`}
                </td>
              </tr>
            ),
          )}
        </tbody>
      </table>
    </GeneralChart>
  );
};

export default NearCity;
